import { useState, useRef } from "react";
import { CONFIG, BANKS, MONTHS } from "./config";
import "./App.css";
import "./chf-ai.css";

const plnFmt = new Intl.NumberFormat("pl-PL", {
  style: "currency",
  currency: "PLN",
  maximumFractionDigits: 0,
});
const pln2 = new Intl.NumberFormat("pl-PL", {
  style: "currency",
  currency: "PLN",
  maximumFractionDigits: 2,
});
const num = (v) => parseFloat(String(v).replace(/\s/g, "").replace(",", "."));

// typ leada w CRM wg waluty — NIGDY nie "nieznana"/puste/inny śmieć z AI;
// jeśli to nie CHF/EUR/USD, ląduje jako CHF (domyślna waluta tego kalkulatora)
const LEAD_TYPE_BY_CURRENCY = { CHF: "CHF", EUR: "EURO", USD: "USD" };
const leadTypeFromCurrency = (currency) =>
  LEAD_TYPE_BY_CURRENCY[String(currency || "").trim().toUpperCase()] || "CHF";

// "350000" -> "350 000" (grupowanie tysięcy w trakcie pisania)
const groupAmount = (raw) => {
  const clean = String(raw).replace(/[^\d.,]/g, "");
  const [int, dec] = clean.replace(",", ".").split(".");
  const grouped = (int || "").replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  return dec !== undefined ? `${grouped},${dec.slice(0, 2)}` : grouped;
};

/* ---------- ikony ---------- */
const Check = (p) => (
  <svg
    width={p.s || 18}
    height={p.s || 18}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 6 9 17l-5-5" />
  </svg>
);
const Arrow = () => (
  <svg
    width="17"
    height="17"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);
const Back = () => (
  <svg
    width="17"
    height="17"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M19 12H5M11 18l-6-6 6-6" />
  </svg>
);
const Pen = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
  </svg>
);

const Clip = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48" />
  </svg>
);
const FileIc = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
    <path d="M14 2v6h6" />
  </svg>
);
const X = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
  >
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);

const EMPTY = {
  year: "",
  month: "",
  currency: "CHF",
  amount: "",
  termYears: "", // wartość z inputu (lata) — tylko UI
  term: "", // wartość kanoniczna w miesiącach — wysyłana do API
  margin: "",
  repaid: "",
  name: "",
  email: "",
  phone: "",
  consent: false,
};

const STEPS = ["Bank", "Twój kredyt", "Kontakt", "Wynik"];
const STEPS_AI = ["Sposób", "Umowa", "Kontakt", "Wynik"];

/* ---------- załączniki (dokumenty do symulacji) ---------- */
const DOC_MAX_FILES = 15;
const DOC_MAX_MB = 15;
const DOC_ACCEPT = ".pdf,.jpg,.jpeg,.png,.heic,.doc,.docx";
const DOC_ACCEPT_RE = /\.(pdf|jpe?g|png|heic|docx?)$/i;

const fmtSize = (b) =>
  b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.ceil(b / 1024)} KB`;

export default function App() {
  const [step, setStep] = useState(1);
  const [mode, setMode] = useState("calc"); // calc = wyliczenia z danych | ai = analiza umowy AI
  const [bank, setBank] = useState(null);
  const [amtMode, setAmtMode] = useState("PLN"); // PLN | FCY
  const [isRepaid, setIsRepaid] = useState(false); // czy kredyt spłacony w całości
  const [f, setF] = useState(EMPTY);
  const [errs, setErrs] = useState({});
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [errBanner, setErrBanner] = useState("");
  const [docs, setDocs] = useState([]); // pliki wybrane przez użytkownika
  const [docErr, setDocErr] = useState("");
  const [docsNotice, setDocsNotice] = useState(""); // łagodny komunikat na kroku wyniku
  const [dragOver, setDragOver] = useState(false);
  const topRef = useRef(null);
  const fileInputRef = useRef(null);

  // --- Ścieżka 1: wynik analizy AI, gdy wejście = skan umowy ---
  const [aiAnalysis, setAiAnalysis] = useState(null);

  // --- leadId z CRM + stan analizy umowy AI "po fakcie" (ścieżka 2) ---
  const [leadId, setLeadId] = useState(null);
  const [contractDocs, setContractDocs] = useState([]);
  const [contractErr, setContractErr] = useState("");
  const [contractBusy, setContractBusy] = useState(false);
  const [contractAnalysis, setContractAnalysis] = useState(null);
  const contractInputRef = useRef(null);

  // dodawanie plików (z inputu lub drag & drop) z walidacją typu/rozmiaru/limitu
  const addDocs = (fileList) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;
    const problems = [];
    setDocs((prev) => {
      const next = [...prev];
      for (const file of incoming) {
        if (next.length >= DOC_MAX_FILES) {
          problems.push(`Można dodać maksymalnie ${DOC_MAX_FILES} plików.`);
          break;
        }
        if (!DOC_ACCEPT_RE.test(file.name)) {
          problems.push(`„${file.name}” — dozwolone: PDF, JPG, PNG, DOC.`);
          continue;
        }
        if (file.size > DOC_MAX_MB * 1048576) {
          problems.push(`„${file.name}” przekracza ${DOC_MAX_MB} MB.`);
          continue;
        }
        if (next.some((d) => d.name === file.name && d.size === file.size)) {
          continue; // duplikat — pomiń bez komunikatu
        }
        next.push(file);
      }
      return next;
    });
    setDocErr(problems.join(" "));
    if (fileInputRef.current) fileInputRef.current.value = ""; // pozwól dodać ten sam plik ponownie
  };

  const removeDoc = (i) => {
    setDocs((prev) => prev.filter((_, idx) => idx !== i));
    setDocErr("");
  };

  const upd = (k, v) => {
    setF((s) => ({ ...s, [k]: v }));
    setErrs((e) => ({ ...e, [k]: false }));
  };

  // input w latach, ale w stanie trzymamy miesiące
  const updTerm = (v) => {
    const years = num(v);
    setErrs((e) => ({ ...e, term: false }));
    setF((s) => ({
      ...s,
      termYears: v,
      term: years > 0 ? Math.round(years * 12) : "",
    }));
  };

  const years = (() => {
    const y = new Date().getFullYear();
    return Array.from({ length: y - 1999 }, (_, i) => y - i);
  })();

  const goStep = (n) => {
    setStep(n);
    setTimeout(() => topRef.current?.scrollIntoView({ block: "start" }), 0);
  };

  const focusFirstError = () =>
    setTimeout(
      () => document.querySelector(".bad")?.scrollIntoView({ block: "center" }),
      0
    );

  // wybór banku = automatyczne przejście dalej (mniej klikania)
  const pickBank = (name) => {
    setBank(name);
    setErrs((e) => ({ ...e, bank: false }));
    goStep(2);
  };

  function validateLoan() {
    const e = {};
    if (!f.year) e.year = true;
    if (!f.month) e.month = true;
    if (!(num(f.amount) > 0)) e.amount = true;
    const t = num(f.termYears);
    if (!(t > 0 && t <= 50)) e.term = true;
    if (isRepaid && !f.repaid) e.repaid = true;
    setErrs(e);
    return Object.keys(e).length === 0;
  }

  function validateContact() {
    const e = {};
    if (f.name.trim().length < 3) e.name = true;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) e.email = true;
    if (f.phone.replace(/[^\d]/g, "").length < 9) e.phone = true;
    if (!f.consent) e.consent = true;
    setErrs(e);
    return Object.keys(e).length === 0;
  }

  const next2 = () => {
    if (!validateLoan()) {
      focusFirstError();
      return;
    }
    goStep(3);
  };

  function buildCalcPayload() {
    const amount = num(f.amount);
    const startDate = `${f.year}-${String(f.month).padStart(2, "0")}-01`;
    const marginRaw = num(f.margin);
    const p = {
      currency: f.currency,
      startDate,
      loanTermMonths: Math.round(num(f.term)), // f.term już w miesiącach
      bankName: bank,
      installmentType: "równe",
      margin: marginRaw > 0 ? marginRaw : 1.25, // server dzieli przez 100 gdy >1
    };
    if (amtMode === "PLN") p.loanAmountPLN = amount;
    else p.loanAmount = amount;
    if (isRepaid && f.repaid) p.repaidDate = f.repaid;
    return p;
  }

  // notatka dołączana do leada w CRM
  function buildLeadNote(res, docsInfo) {
    const parts = [
      "Lead z kalkulatora CHF (kppp.pl).",
      `Uruchomienie: ${f.year}-${String(f.month).padStart(2, "0")}.`,
      `Okres: ${f.termYears} lat.`,
      `Marża: ${f.margin || "1.25"}%.`,
    ];
    if (res) {
      parts.push(
        `Łączna korzyść: ${plnFmt.format(res.lacznaKorzysc || 0)}.`,
        `Nadpłata: ${plnFmt.format(res.nadplata || 0)}.`,
        `Spadek salda: ${plnFmt.format(res.zmniejszenieSalda || 0)}.`,
        `Saldo wg banku: ${plnFmt.format(res.currentBalancePLN || 0)}.`,
        `Rat zapłaconych: ${res.paidInstallments ?? "—"}.`,
        res.uuid ? `UUID analizy: ${res.uuid}.` : ""
      );
    } else {
      parts.push(
        "Analiza nie policzyła się — kontakt zachowany do oddzwonienia."
      );
    }
    if (docs.length) {
      const names = docs.map((d) => d.name).join(", ");
      parts.push(`Dokumenty klienta (${docs.length}): ${names}.`);
      if (docsInfo?.failed?.length) {
        parts.push(
          `Nie udało się przesłać plików: ${(
            docsInfo.failedDetails || docsInfo.failed
          ).join("; ")} — poprosić o nie przy kontakcie.`
        );
      }
    }
    return parts.filter(Boolean).join(" ");
  }

  // body zgodne z api.kppp.pl/addLeadWeb
  function buildLead(res, docsInfo) {
    return {
      name: f.name.trim(),
      phone: f.phone.trim(),
      mail: f.email.trim(),
      source: CONFIG.LEAD_SOURCE || "KPPP",
      bank: bank,
      agreement: "",
      creditAmmount: res?.loanAmountPLN ?? num(f.amount),
      creditCurrency: f.currency, // CHF / EUR / USD
      creditDate: `${f.year}-${String(f.month).padStart(2, "0")}-01`,
      addUser: CONFIG.LEAD_USER || "Admin KPPP",
      type: leadTypeFromCurrency(f.currency),
      end: (isRepaid && f.repaid) || "",
      length: Math.round(num(f.termYears)) || "",
      note: buildLeadNote(res, docsInfo),
      /* addLeadWeb robi req.body.files.split(",") — files MUSI być stringiem
         URL-i rozdzielonych przecinkami (bez spacji!). Symulacja fx_*.xlsx
         + hostowane dokumenty klienta lądują razem w sellFiles leada. */
      files: [res?.excelUrl, ...(docsInfo?.urls || [])]
        .filter(Boolean)
        .join(","),
    };
  }

  /* Hosting dokumentów: każdy plik idzie multipartem (pole "file") na
     własny proxy /api/leads/kppp/upload (server.js), który dokleja
     KPPP_KEY po swojej stronie i przekazuje dalej do api.kppp.pl —
     front zna tylko swój X-API-Key. Serwer odpowiada gołym URL-em;
     zebrane URL-e trafiają do pola files w addLeadWeb. Błąd uploadu
     nie blokuje ani wyniku, ani leada. */
  async function uploadDocs() {
    if (!docs.length) return { urls: [], failed: [], failedDetails: [] };
    const endpoint =
      CONFIG.API_BASE + (CONFIG.UPLOAD_ENDPOINT || "/api/leads/kppp/upload");
    const results = await Promise.allSettled(
      docs.map(async (file) => {
        const fd = new FormData();
        fd.append("file", file, file.name);
        let res;
        try {
          res = await fetch(endpoint, {
            method: "POST",
            headers: { "X-API-Key": CONFIG.API_KEY }, // bez Content-Type — ustawi go FormData
            body: fd,
          });
        } catch (e) {
          // fetch rzuca bez statusu przy problemie sieciowym/CORS
          throw new Error(`sieć/CORS: ${e.message}`);
        }
        const body = (await res.text().catch(() => "")).trim();
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}: ${body.slice(0, 200)}`);
        }
        if (!/^(https?:)?\//.test(body)) {
          throw new Error(`zła odpowiedź serwera: ${body.slice(0, 200)}`);
        }
        return body;
      })
    );
    const urls = [];
    const failed = [];
    const failedDetails = []; // "nazwa — powód" do notatki i maila wewnętrznego
    results.forEach((r, i) => {
      if (r.status === "fulfilled") urls.push(r.value);
      else {
        const reason = r.reason?.message || String(r.reason);
        failed.push(docs[i].name);
        failedDetails.push(`${docs[i].name} — ${reason}`);
        console.warn(`[uploadDocs] ${docs[i].name} → ${reason}`);
      }
    });
    return { urls, failed, failedDetails };
  }

  async function callFx(payload) {
    const res = await fetch(CONFIG.API_BASE + CONFIG.FX_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-API-Key": CONFIG.API_KEY,
      },
      body: JSON.stringify(payload),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `Błąd serwera (${res.status}).`);
    return data;
  }

  // rejestracja konwersji Google Ads (odpalana po dodaniu leada do CRM)
  function trackConversion() {
    if (typeof window === "undefined" || typeof window.gtag !== "function")
      return;
    window.gtag("event", "conversion", {
      send_to: "AW-999769198/AkGTCO-g7PgYEO6I3dwD",
      event_callback: () => {}, // brak nawigacji do wstrzymania — no-op
    });
  }

  async function sendLead(lead) {
    if (!CONFIG.SEND_LEAD) return;
    try {
      const res = await fetch(
        CONFIG.API_BASE + (CONFIG.LEAD_ENDPOINT || "/api/leads/kppp"),
        {
          method: "POST",
          keepalive: true, // żądanie przeżyje zamknięcie karty tuż po wyniku
          headers: {
            "Content-Type": "application/json",
            "X-API-Key": CONFIG.API_KEY,
          },
          body: JSON.stringify(lead),
        }
      );
      if (res.ok) {
        trackConversion(); // konwersja dopiero po udanym zapisie w CRM
        // CRM (po patchu 1) zwraca { ok, leadId } zamiast zwykłego "ok" —
        // parsujemy defensywnie, żeby nie wywalić się, jeśli patch jeszcze
        // nie wszedł na produkcję.
        try {
          const body = await res.clone().json();
          if (body?.leadId) setLeadId(body.leadId);
        } catch (_) {
          /* stara odpowiedź tekstowa "ok" — brak leadId, ścieżka 2 (upload
             umowy po fakcie) po prostu się nie pokaże */
        }
      }
    } catch (_) {
      /* nie blokuj wyniku, gdy CRM chwilowo niedostępny */
    }
  }

  async function uploadContractFiles(files) {
    const endpoint =
      CONFIG.API_BASE + (CONFIG.UPLOAD_ENDPOINT || "/api/leads/kppp/upload");
    const results = await Promise.allSettled(
      files.map(async (file) => {
        const fd = new FormData();
        fd.append("file", file, file.name);
        const res = await fetch(endpoint, {
          method: "POST",
          headers: { "X-API-Key": CONFIG.API_KEY },
          body: fd,
        });
        const body = (await res.text().catch(() => "")).trim();
        if (!res.ok || !/^(https?:)?\//.test(body)) {
          throw new Error(`upload nieudany: ${body.slice(0, 200)}`);
        }
        return body;
      })
    );
    return results;
  }

  // wspólne wywołanie endpointu analizy AI (ścieżka 1 i 2)
  async function callContractAnalyze(fileUrls, withLeadId) {
    const res = await fetch(
      CONFIG.API_BASE +
        (CONFIG.CONTRACT_ANALYZE_ENDPOINT || "/api/chf/analyze-contract"),
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": CONFIG.API_KEY,
        },
        body: JSON.stringify({
          fileUrls,
          ...(withLeadId ? { leadId: withLeadId } : {}),
        }),
      }
    );
    const data = await res.json().catch(() => ({}));
    if (!res.ok)
      throw new Error(data.error || `Błąd analizy umowy (${res.status}).`);
    return data;
  }

  // --- Ścieżka 2: analiza AI umowy PO wyniku/leadzie ---
  const addContractDocs = (fileList) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;
    const problems = [];
    setContractDocs((prev) => {
      const next = [...prev];
      for (const file of incoming) {
        if (next.length >= DOC_MAX_FILES) {
          problems.push(`Można dodać maksymalnie ${DOC_MAX_FILES} plików.`);
          break;
        }
        if (!DOC_ACCEPT_RE.test(file.name)) {
          problems.push(`„${file.name}” — dozwolone: PDF, JPG, PNG, DOC.`);
          continue;
        }
        if (file.size > DOC_MAX_MB * 1048576) {
          problems.push(`„${file.name}” przekracza ${DOC_MAX_MB} MB.`);
          continue;
        }
        next.push(file);
      }
      return next;
    });
    setContractErr(problems.join(" "));
    if (contractInputRef.current) contractInputRef.current.value = "";
  };

  const removeContractDoc = (i) => {
    setContractDocs((prev) => prev.filter((_, idx) => idx !== i));
    setContractErr("");
  };

  async function analyzeContractForLead() {
    if (!leadId || !contractDocs.length || contractBusy) return;
    setContractBusy(true);
    setContractErr("");
    try {
      // 1) upload skanów tym samym mechanizmem co dziś (uploadLeadFileWeb)
      const uploaded = await uploadContractFiles(contractDocs);
      const urls = uploaded
        .filter((r) => r.status === "fulfilled")
        .map((r) => r.value);
      const failedCount = uploaded.length - urls.length;
      if (!urls.length) {
        throw new Error("Nie udało się przesłać żadnego pliku umowy.");
      }

      // 2) AI-analiza przypięta do leadId (ekstrakcja + kalkulacja +
      //    klauzule + kwalifikacja); leadId trafia też do tabeli audytowej
      const aiData = await callContractAnalyze(urls, leadId);

      // 3) doklej pliki do ISTNIEJĄCEGO leada w CRM (appendLeadFileWeb,
      //    patch 2); notatkę z wynikiem AI dopinamy przy pierwszym pliku,
      //    żeby nie dublować jej w note leada
      const appendEndpoint =
        CONFIG.API_BASE +
        (CONFIG.APPEND_LEAD_ENDPOINT || "/api/leads/kppp/append");
      const appends = await Promise.allSettled(
        urls.map((url, i) =>
          fetch(appendEndpoint, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-API-Key": CONFIG.API_KEY,
            },
            body: JSON.stringify({
              leadId,
              file: url,
              addUser: CONFIG.LEAD_USER || "Admin KPPP",
              ...(i === 0 && aiData.noteSummary
                ? { note: aiData.noteSummary }
                : {}),
            }),
          }).then((r) => {
            if (!r.ok) throw new Error(`appendLeadFileWeb HTTP ${r.status}`);
          })
        )
      );
      const appendFailed = appends.filter(
        (r) => r.status === "rejected"
      ).length;

      setContractAnalysis(aiData);
      const issues = [];
      if (failedCount)
        issues.push(
          `Nie przesłano ${failedCount} z ${contractDocs.length} plików.`
        );
      if (appendFailed)
        issues.push(
          "Część plików nie dopięła się do zgłoszenia w systemie — prawnik i tak otrzyma analizę."
        );
      setContractErr(issues.join(" "));
    } catch (err) {
      setContractErr(err.message || "Nie udało się przeanalizować umowy.");
    } finally {
      setContractBusy(false);
    }
  }

  // Ścieżka 1: wejście przez skan umowy — AI wyciąga dane, liczy roszczenie,
  // wskazuje klauzule (dopasowane do mapy Rzecznika Finansowego), a lead
  // idzie ISTNIEJĄCĄ ścieżką "lead z dokumentami".
  async function onAnalyzeAi() {
    if (!validateContact()) {
      focusFirstError();
      return;
    }
    setLoading(true);
    setErrBanner("");
    let docsInfo = { urls: [], failed: [], failedDetails: [] };
    let ai = null;
    try {
      // 1) upload skanów istniejącym mechanizmem
      docsInfo = await uploadDocs();
      if (!docsInfo.urls.length) {
        throw new Error(
          "Nie udało się przesłać żadnego pliku umowy — spróbuj ponownie."
        );
      }

      // 2) analiza AI: ekstrakcja + kalkulacja + klauzule + kwalifikacja
      ai = await callContractAnalyze(docsInfo.urls);
      setAiAnalysis(ai);

      // 3) prefill stanu danymi z ekstrakcji, żeby wynik (Result),
      //    notatka i mail używały realnych danych umowy
      const ex = ai.extracted || {};
      if (ex.bankName) setBank(ex.bankName);
      const d = /^(\d{4})-(\d{2})-\d{2}$/.exec(ex.agreementDate || "");
      const hasPLN = Number(ex.loanAmountPLN) > 0;
      setF((s) => ({
        ...s,
        year: d ? d[1] : s.year,
        month: d ? String(parseInt(d[2], 10)) : s.month,
        currency: ["CHF", "EUR", "USD"].includes(ex.currency)
          ? ex.currency
          : s.currency,
        amount: hasPLN
          ? String(ex.loanAmountPLN)
          : ex.loanAmount
          ? String(ex.loanAmount)
          : s.amount,
        termYears: ex.loanTermMonths
          ? String(Math.round(ex.loanTermMonths / 12))
          : s.termYears,
        term: ex.loanTermMonths ? String(ex.loanTermMonths) : s.term,
        margin: ex.marginPercent ? String(ex.marginPercent) : s.margin,
        repaid: ex.repaidDate || s.repaid,
      }));
      if (hasPLN) setAmtMode("PLN");
      else if (ex.loanAmount) setAmtMode("FCY");
      if (ex.repaidDate) setIsRepaid(true);

      // 4) lead z dokumentami — jak dziś, tylko note dostaje sekcję AI
      const calc = ai.calc || null;
      const lead = {
        name: f.name.trim(),
        phone: f.phone.trim(),
        mail: f.email.trim(),
        source: CONFIG.LEAD_SOURCE || "KPPP",
        bank: ex.bankName || bank || "do ustalenia (analiza AI)",
        agreement: "",
        creditAmmount: Number(ex.loanAmountPLN) || Number(ex.loanAmount) || 0,
        creditCurrency: hasPLN
          ? "PLN"
          : LEAD_TYPE_BY_CURRENCY[String(ex.currency || "").trim().toUpperCase()]
            ? ex.currency
            : "CHF",
        creditDate: ex.agreementDate || "",
        addUser: CONFIG.LEAD_USER || "Admin KPPP",
        type: leadTypeFromCurrency(ex.currency),
        end: ex.repaidDate || "",
        length: Math.round((Number(ex.loanTermMonths) || 0) / 12) || "",
        note:
          "Lead z analizy AI umowy (kppp.pl). " +
          (ai.noteSummary || "") +
          (docsInfo.failed?.length
            ? ` Nie przesłano plików: ${(
                docsInfo.failedDetails || docsInfo.failed
              ).join("; ")}.`
            : ""),
        files: [calc?.excelUrl, ...docsInfo.urls].filter(Boolean).join(","),
      };
      await sendLead(lead);
      // UWAGA: celowo NIE calcMailFields() — stan (f/bank/amtMode) w tym
      // miejscu jeszcze nie odzwierciedla setBank/setF powyżej (React
      // aktualizuje go dopiero przy kolejnym renderze), więc budujemy pola
      // maila wprost z ekstrakcji AI (ex), tak jak lead wyżej.
      sendCalcMails(
        {
          name: f.name.trim(),
          email: f.email.trim(),
          phone: f.phone.trim(),
          bank: ex.bankName || bank || "",
          year: d ? d[1] : "",
          month: d ? MONTHS[parseInt(d[2], 10) - 1] || d[2] : "",
          currency: ["CHF", "EUR", "USD"].includes(ex.currency)
            ? ex.currency
            : "CHF",
          amount: hasPLN
            ? `${ex.loanAmountPLN} PLN`
            : ex.loanAmount
            ? `${ex.loanAmount} ${ex.currency || "CHF"}`
            : "",
          termYears: ex.loanTermMonths
            ? String(Math.round(ex.loanTermMonths / 12))
            : "",
          repaid: ex.repaidDate || "",
          docs: docs.map((doc) => doc.name),
        },
        docsInfo
      );

      setResult(calc); // może być null, gdy AI nie odczytało kompletu liczb
      if (!calc && ai.calcError) {
        setErrBanner(
          "Wirtualny asystent prawny przeanalizował umowę, ale nie wyliczył szacunkowej korzyści: " +
            ai.calcError +
            " Twoje zgłoszenie i skan umowy trafiły już do prawnika."
        );
      }
      goStep(4);
    } catch (err) {
      // lead ZAWSZE ląduje w CRM — nawet gdy analiza AI padła
      sendLead(buildLead(null, docsInfo));
      sendCalcMails(calcMailFields(), docsInfo);
      setResult(null);
      setErrBanner(
        "Nie udało się przeprowadzić analizy umowy: " +
          err.message +
          " Twoje zgłoszenie zostało zapisane — prawnik przeanalizuje umowę ręcznie."
      );
      goStep(4);
    } finally {
      setLoading(false);
    }
  }

  // pola z formularza kalkulatora (ścieżka "calc") — wywołujące zawsze
  // aktualny stan, bo w tej ścieżce nic go nie nadpisuje asynchronicznie
  function calcMailFields() {
    return {
      name: f.name.trim(),
      email: f.email.trim(),
      phone: f.phone.trim(),
      bank: bank,
      year: f.year,
      month: MONTHS[parseInt(f.month, 10) - 1] || f.month,
      currency: f.currency,
      amount: `${f.amount} ${amtMode === "PLN" ? "PLN" : f.currency}`,
      termYears: f.termYears,
      repaid: (isRepaid && f.repaid) || "",
      docs: docs.map((d) => d.name),
    };
  }

  /* Maile po zgłoszeniu (powiadomienie wewnętrzne + potwierdzenie do
     klienta) — wysyła je serwer przez /sendCalcMailsWeb (SMTP nazwa.pl).
     Fire-and-forget: porażka maili nie blokuje wyniku ani leada.
     `fields` MUSI być podane jawnie (nie czytane ze stanu w zamknięciu) —
     w ścieżce AI stan (f/bank/amtMode) jest nadpisywany przez setF/setBank
     tuż przed wysyłką maila, a React aktualizuje go dopiero przy kolejnym
     renderze, więc odczyt ze stanu w tym miejscu dawałby puste dane. */
  function sendCalcMails(fields, docsInfo) {
    if (!CONFIG.SEND_LEAD) return; // te same warunki co lead (tryb testowy)
    fetch(CONFIG.API_BASE + (CONFIG.MAILS_ENDPOINT || "/api/leads/kppp/mail"), {
      method: "POST",
      keepalive: true, // żądanie przeżyje zamknięcie karty tuż po wyniku
      headers: {
        "Content-Type": "application/json",
        "X-API-Key": CONFIG.API_KEY,
      },
      body: JSON.stringify({
        ...fields,
        // faktyczny wynik przesyłania — mail przestaje sugerować,
        // że dokumenty dotarły, gdy upload się nie powiódł
        docsUploaded: docsInfo?.urls || [],
        docsFailed: docsInfo?.failedDetails || [],
      }),
    }).catch((e) => console.warn("[sendCalcMails]", e.message));
  }

  async function onCalc() {
    if (!validateContact()) {
      focusFirstError();
      return;
    }
    setLoading(true);
    setErrBanner("");
    const params = buildCalcPayload();
    const uploading = uploadDocs(); // hosting plików równolegle z liczeniem
    const noticeFrom = (info) =>
      setDocsNotice(
        info?.failed?.length
          ? `Nie udało się przesłać ${info.failed.length} z ${docs.length} ` +
              `dokumentów (${info.failed.join(", ")}). Nic straconego — ` +
              "poprosimy o nie przy kontakcie."
          : ""
      );
    try {
      const [res, docsInfo] = await Promise.all([callFx(params), uploading]);
      noticeFrom(docsInfo);
      sendLead(buildLead(res, docsInfo));
      sendCalcMails(calcMailFields(), docsInfo);
      setResult(res);
      goStep(4);
    } catch (err) {
      const docsInfo = await uploading.catch(() => ({
        urls: [],
        failed: [],
        failedDetails: [],
      }));
      noticeFrom(docsInfo);
      sendLead(buildLead(null, docsInfo)); // zachowaj kontakt mimo błędu liczenia
      sendCalcMails(calcMailFields(), docsInfo);
      setResult(null);
      setErrBanner(
        "Nie udało się policzyć analizy: " +
          err.message +
          " Sprawdź dane lub spróbuj ponownie — nasz prawnik i tak otrzyma Twoje zgłoszenie."
      );
      goStep(4);
    } finally {
      setLoading(false);
    }
  }

  const reset = () => {
    setStep(1);
    setBank(null);
    setAmtMode("PLN");
    setIsRepaid(false);
    setF(EMPTY);
    setErrs({});
    setResult(null);
    setErrBanner("");
    setDocs([]);
    setDocErr("");
    setDocsNotice("");
    setMode("calc");
    setAiAnalysis(null);
    setLeadId(null);
    setContractDocs([]);
    setContractErr("");
    setContractAnalysis(null);
    setTimeout(() => topRef.current?.scrollIntoView({ block: "start" }), 0);
  };

  const startInfo =
    f.year && f.month
      ? `${MONTHS[parseInt(f.month, 10) - 1] || ""} ${f.year}`
      : "—";

  return (
    <div className="kppp-calc" id="analiza" ref={topRef}>
      <div className="hero-mode">
        <h2 className="hero-mode-title">Jak chcesz sprawdzić swój kredyt?</h2>
        <p className="hero-mode-sub" style={{ color: "white !important" }}>
          Sprawy frankowe (i inne kredyty walutowe) dotyczą umów indeksowanych
          lub denominowanych do CHF/EUR/USD. Sąd może unieważnić umowę albo
          nakazać zwrot nadpłaconych kwot.
        </p>
        <br />

        <div className="mode-grid">
          <button
            type="button"
            className={`mode-tile${mode === "calc" ? " sel" : ""}`}
            onClick={() => {
              setMode("calc");
              goStep(1);
            }}
          >
            <b>Wyliczenia z danych umowy</b>
            <small>
              podasz bank, kwotę i okres — policzymy szacunkową korzyść
            </small>
          </button>
          <button
            type="button"
            className={`mode-tile${mode === "ai" ? " sel" : ""}`}
            onClick={() => {
              setMode("ai");
              setBank(null);
              goStep(2);
            }}
          >
            <b>Wirtualny asystent prawny</b>
            <small>
              prześlij skan umowy — odczyta dane, policzy korzyść i sprawdzi
              klauzule w mapie Rzecznika Finansowego
            </small>
          </button>
        </div>
      </div>

      <div className="panel">
        {/* pasek postępu w stylu kroków KPPP */}
        <div className="stepper" role="list" aria-label="Postęp analizy">
          {(mode === "ai" ? STEPS_AI : STEPS).map((label, i) => {
            const n = i + 1;
            const st = n === step ? "active" : n < step ? "done" : "";
            return (
              <div key={label} role="listitem" className={`snode ${st}`}>
                <span className="dot">{n < step ? <Check s={14} /> : n}</span>
                <span className="slab">{label}</span>
              </div>
            );
          })}
          <div
            className="sline"
            style={{ "--p": `${((step - 1) / (STEPS.length - 1)) * 100}%` }}
          />
        </div>

        <div className="panel-body">
          {/* ================= KROK 1 — BANK ================= */}
          {step === 1 && (
            <div className="step">
              <h2 className="step-title">W którym banku zaciągnięto kredyt?</h2>
              <p className="step-sub">
                Zwróć uwagę na następstwo prawne — bank, w którym zawierano
                umowę, może mieć dziś innego następcę i inną nazwę (podpowiedzi
                w nawiasach).
              </p>
              <div className="bank-grid">
                {BANKS.map(([name, succ], i) => (
                  <button
                    key={i}
                    type="button"
                    className={`bank${bank === name ? " sel" : ""}`}
                    onClick={() => pickBank(name)}
                  >
                    <b>{name}</b>
                    {succ && <small>{succ}</small>}
                  </button>
                ))}
              </div>
              {errs.bank && (
                <div className="err">Wybierz bank, aby przejść dalej.</div>
              )}
            </div>
          )}

          {/* ============ KROK 2 (AI) — SKAN UMOWY ============ */}
          {step === 2 && mode === "ai" && (
            <div className="step">
              <h2 className="step-title">Prześlij skan umowy kredytu</h2>
              <p className="step-sub">
                Wirtualny asystent prawny odczyta z umowy bank, kwotę, walutę i
                okres, policzy szacowaną korzyść i sprawdzi zapisy umowy w mapie
                klauzul niedozwolonych Rzecznika Finansowego.
              </p>

              <div className="grid">
                <div className="full">
                  <div
                    className={`dropzone${dragOver ? " drag" : ""}`}
                    onClick={() => fileInputRef.current?.click()}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        fileInputRef.current?.click();
                      }
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setDragOver(true);
                    }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setDragOver(false);
                      addDocs(e.dataTransfer.files);
                    }}
                    role="button"
                    tabIndex={0}
                    aria-label="Dodaj skan umowy do analizy"
                  >
                    <span className="dz-ic">
                      <Clip />
                    </span>
                    <span className="dz-txt">
                      <b>Kliknij, aby dodać skan umowy</b> lub przeciągnij go
                      tutaj
                      <small>
                        Umowa kredytu (wszystkie strony), ew. harmonogram · PDF,
                        JPG, PNG, DOC · do {DOC_MAX_FILES} plików, maks.{" "}
                        {DOC_MAX_MB} MB każdy
                      </small>
                    </span>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept={DOC_ACCEPT}
                      hidden
                      onChange={(e) => addDocs(e.target.files)}
                    />
                  </div>
                  {docErr && <div className="err">{docErr}</div>}
                  {errs.aiDocs && (
                    <div className="err">
                      Dodaj przynajmniej jeden plik umowy, aby przejść dalej.
                    </div>
                  )}
                  {docs.length > 0 && (
                    <ul className="doc-list">
                      {docs.map((d, i) => (
                        <li key={`${d.name}-${d.size}`} className="doc-item">
                          <span className="doc-ic">
                            <FileIc />
                          </span>
                          <span className="doc-name" title={d.name}>
                            {d.name}
                          </span>
                          <span className="doc-size num">
                            {fmtSize(d.size)}
                          </span>
                          <button
                            type="button"
                            className="doc-del"
                            onClick={() => removeDoc(i)}
                            aria-label={`Usuń plik ${d.name}`}
                          >
                            <X />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  <p className="lab-note">
                    Dokumenty przekazujemy wyłącznie prawnikowi prowadzącemu
                    Twoją sprawę.
                  </p>
                </div>

                <div className="full">
                  <label className="lab">
                    Czy kredyt został już spłacony w całości?
                  </label>
                  <div className="seg seg-narrow" role="group">
                    <button
                      type="button"
                      className={!isRepaid ? "on" : ""}
                      onClick={() => {
                        setIsRepaid(false);
                        upd("repaid", "");
                      }}
                    >
                      Nie, nadal spłacam
                    </button>
                    <button
                      type="button"
                      className={isRepaid ? "on" : ""}
                      onClick={() => setIsRepaid(true)}
                    >
                      Tak, spłacony
                    </button>
                  </div>
                  <p className="lab-note">
                    Nawet po całkowitej spłacie możesz odzyskać nadpłacone
                    pieniądze.
                  </p>
                </div>

                {isRepaid && (
                  <div className="full reveal">
                    <label className="lab" htmlFor="repaid-ai">
                      Data całkowitej spłaty <span className="req">*</span>
                      <span className="hint">
                        przybliżona, jeśli nie pamiętasz
                      </span>
                    </label>
                    <input
                      id="repaid-ai"
                      type="date"
                      className={errs.repaid ? "bad" : ""}
                      value={f.repaid}
                      onChange={(e) => upd("repaid", e.target.value)}
                    />
                    {errs.repaid && (
                      <div className="err">Podaj datę spłaty kredytu.</div>
                    )}
                  </div>
                )}
              </div>

              <div className="nav-row">
                <button
                  className="btn btn-ghost"
                  onClick={() => {
                    setMode("calc");
                    goStep(1);
                  }}
                >
                  <Back />
                  Zmień sposób
                </button>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    const e = {};
                    if (!docs.length) e.aiDocs = true;
                    if (isRepaid && !f.repaid) e.repaid = true;
                    setErrs(e);
                    if (Object.keys(e).length) {
                      focusFirstError();
                      return;
                    }
                    goStep(3);
                  }}
                >
                  Przejdź dalej
                  <Arrow />
                </button>
              </div>
            </div>
          )}

          {/* ================= KROK 2 — DANE KREDYTU ================= */}
          {step === 2 && mode === "calc" && (
            <div className="step">
              <h2 className="step-title">Dane z umowy kredytowej</h2>
              <p className="step-sub">
                Miej umowę pod ręką — wystarczą 4 informacje. Data uruchomienia
                jest kluczowa: od niej zależy kurs, po którym bank przeliczył
                Twój kredyt.
              </p>

              <div className="grid">
                <div>
                  <label className="lab" htmlFor="year">
                    Rok zaciągnięcia kredytu <span className="req">*</span>
                  </label>
                  <select
                    id="year"
                    className={errs.year ? "bad" : ""}
                    value={f.year}
                    onChange={(e) => upd("year", e.target.value)}
                  >
                    <option value="">— wybierz —</option>
                    {years.map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                  {errs.year && <div className="err">Wybierz rok.</div>}
                </div>
                <div>
                  <label className="lab" htmlFor="month">
                    Miesiąc zaciągnięcia <span className="req">*</span>
                  </label>
                  <select
                    id="month"
                    className={errs.month ? "bad" : ""}
                    value={f.month}
                    onChange={(e) => upd("month", e.target.value)}
                  >
                    <option value="">— wybierz —</option>
                    {MONTHS.map((m, i) => (
                      <option key={i} value={i + 1}>
                        {m}
                      </option>
                    ))}
                  </select>
                  {errs.month && <div className="err">Wybierz miesiąc.</div>}
                </div>

                <div>
                  <label className="lab" htmlFor="currency">
                    Waluta z umowy <span className="req">*</span>
                  </label>
                  <select
                    id="currency"
                    value={f.currency}
                    onChange={(e) => upd("currency", e.target.value)}
                  >
                    <option value="CHF">CHF — frank szwajcarski</option>
                    <option value="EUR">EUR — euro</option>
                    <option value="USD">USD — dolar amerykański</option>
                  </select>
                </div>
                <div>
                  <label className="lab">
                    Kwota z umowy podana w <span className="req">*</span>
                  </label>
                  <div
                    className="seg"
                    role="group"
                    aria-label="Jednostka kwoty"
                  >
                    <button
                      type="button"
                      className={amtMode === "PLN" ? "on" : ""}
                      onClick={() => setAmtMode("PLN")}
                    >
                      złotych (PLN)
                    </button>
                    <button
                      type="button"
                      className={amtMode === "FCY" ? "on" : ""}
                      onClick={() => setAmtMode("FCY")}
                    >
                      walucie ({f.currency})
                    </button>
                  </div>
                </div>

                <div>
                  <label className="lab" htmlFor="amount">
                    Kwota kredytu z umowy <span className="req">*</span>
                    <span className="hint">
                      {amtMode === "PLN" ? "w PLN" : `w ${f.currency}`}
                    </span>
                  </label>
                  <input
                    id="amount"
                    inputMode="decimal"
                    className={errs.amount ? "bad" : ""}
                    placeholder="np. 350 000"
                    value={f.amount}
                    onChange={(e) => upd("amount", groupAmount(e.target.value))}
                  />
                  {errs.amount && (
                    <div className="err">Podaj kwotę kredytu.</div>
                  )}
                </div>
                <div>
                  <label className="lab" htmlFor="term">
                    Okres kredytowania <span className="req">*</span>
                    <span className="hint">w latach</span>
                  </label>
                  <input
                    id="term"
                    inputMode="numeric"
                    className={errs.term ? "bad" : ""}
                    placeholder="np. 30"
                    value={f.termYears}
                    onChange={(e) => updTerm(e.target.value)}
                  />
                  {errs.term && (
                    <div className="err">Podaj okres w latach (1–50).</div>
                  )}
                </div>

                <div className="full">
                  <label className="lab">
                    Czy kredyt został już spłacony w całości?
                  </label>
                  <div className="seg seg-narrow" role="group">
                    <button
                      type="button"
                      className={!isRepaid ? "on" : ""}
                      onClick={() => {
                        setIsRepaid(false);
                        upd("repaid", "");
                      }}
                    >
                      Nie, nadal spłacam
                    </button>
                    <button
                      type="button"
                      className={isRepaid ? "on" : ""}
                      onClick={() => setIsRepaid(true)}
                    >
                      Tak, spłacony
                    </button>
                  </div>
                  <p className="lab-note">
                    Nawet po całkowitej spłacie możesz odzyskać nadpłacone
                    pieniądze.
                  </p>
                </div>

                {isRepaid && (
                  <div className="full reveal">
                    <label className="lab" htmlFor="repaid">
                      Data całkowitej spłaty <span className="req">*</span>
                      <span className="hint">
                        przybliżona, jeśli nie pamiętasz
                      </span>
                    </label>
                    <input
                      id="repaid"
                      type="date"
                      className={errs.repaid ? "bad" : ""}
                      value={f.repaid}
                      onChange={(e) => upd("repaid", e.target.value)}
                    />
                    {errs.repaid && (
                      <div className="err">Podaj datę spłaty kredytu.</div>
                    )}
                  </div>
                )}

                <div className="full">
                  <details className="adv">
                    <summary>Znam marżę banku (opcjonalnie)</summary>
                    <div className="adv-body">
                      <label className="lab" htmlFor="margin">
                        Marża banku <span className="hint">w %</span>
                      </label>
                      <input
                        id="margin"
                        inputMode="decimal"
                        placeholder="np. 1,25 (domyślnie przyjmujemy 1,25%)"
                        value={f.margin}
                        onChange={(e) => upd("margin", e.target.value)}
                      />
                    </div>
                  </details>
                </div>
              </div>

              <div className="nav-row">
                <button className="btn btn-ghost" onClick={() => goStep(1)}>
                  <Back />
                  Zmień bank
                </button>
                <button className="btn btn-primary" onClick={next2}>
                  Przejdź dalej
                  <Arrow />
                </button>
              </div>
            </div>
          )}

          {/* ================= KROK 3 — KONTAKT ================= */}
          {step === 3 && (
            <div className="step">
              <h2 className="step-title">Wynik analizy jest prawie gotowy</h2>
              <p className="step-sub">
                Zostaw dane kontaktowe — pokażemy szacunkową korzyść od razu, a
                nasz prawnik bezpłatnie omówi z Tobą pełną analizę umowy.
              </p>

              {/* podsumowanie wprowadzonych danych — możliwość korekty */}
              <div className="summary">
                <div className="sum-head">
                  <span>
                    {mode === "ai"
                      ? "Umowa dla wirtualnego asystenta prawnego"
                      : "Twoje dane z umowy"}
                  </span>
                  <button
                    type="button"
                    className="edit-link"
                    onClick={() => goStep(2)}
                  >
                    <Pen /> Popraw
                  </button>
                </div>
                <div className="sum-chips">
                  {mode === "ai" ? (
                    <>
                      {docs.map((d) => (
                        <span className="chip" key={`${d.name}-${d.size}`}>
                          <b>Plik:</b> {d.name}
                        </span>
                      ))}
                      {isRepaid && f.repaid && (
                        <span className="chip">
                          <b>Spłacony:</b> {f.repaid}
                        </span>
                      )}
                    </>
                  ) : (
                    <>
                      <span className="chip">
                        <b>Bank:</b> {bank}
                      </span>
                      <span className="chip">
                        <b>Uruchomienie:</b> {startInfo}
                      </span>
                      <span className="chip">
                        <b>Kwota:</b> {f.amount || "—"}{" "}
                        {amtMode === "PLN" ? "PLN" : f.currency}
                      </span>
                      <span className="chip">
                        <b>Okres:</b> {f.termYears || "—"} lat
                      </span>
                      {isRepaid && f.repaid && (
                        <span className="chip">
                          <b>Spłacony:</b> {f.repaid}
                        </span>
                      )}
                    </>
                  )}
                </div>
              </div>

              <div className="grid">
                <div className="full">
                  <label className="lab" htmlFor="name">
                    Imię i nazwisko <span className="req">*</span>
                  </label>
                  <input
                    id="name"
                    autoComplete="name"
                    className={errs.name ? "bad" : ""}
                    placeholder="Jan Kowalski"
                    value={f.name}
                    onChange={(e) => upd("name", e.target.value)}
                  />
                  {errs.name && (
                    <div className="err">Podaj imię i nazwisko.</div>
                  )}
                </div>
                <div>
                  <label className="lab" htmlFor="email">
                    Adres e-mail <span className="req">*</span>
                  </label>
                  <input
                    id="email"
                    type="email"
                    autoComplete="email"
                    className={errs.email ? "bad" : ""}
                    placeholder="jan.kowalski@email.pl"
                    value={f.email}
                    onChange={(e) => upd("email", e.target.value)}
                  />
                  {errs.email && (
                    <div className="err">Podaj poprawny adres e-mail.</div>
                  )}
                </div>
                <div>
                  <label className="lab" htmlFor="phone">
                    Numer telefonu <span className="req">*</span>
                  </label>
                  <input
                    id="phone"
                    type="tel"
                    autoComplete="tel"
                    className={errs.phone ? "bad" : ""}
                    placeholder="600 700 800"
                    value={f.phone}
                    onChange={(e) => upd("phone", e.target.value)}
                  />
                  {errs.phone && (
                    <div className="err">Podaj poprawny numer telefonu.</div>
                  )}
                </div>

                {/* ---------- dokumenty do symulacji (opcjonalnie) ---------- */}
                {mode === "calc" && (
                  <div className="full">
                    <label className="lab" htmlFor="docs">
                      Dokumenty do analizy{" "}
                      <span className="hint">
                        opcjonalnie — przyspieszą pełną analizę
                      </span>
                    </label>
                    <div
                      className={`dropzone${dragOver ? " drag" : ""}`}
                      onClick={() => fileInputRef.current?.click()}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          fileInputRef.current?.click();
                        }
                      }}
                      onDragOver={(e) => {
                        e.preventDefault();
                        setDragOver(true);
                      }}
                      onDragLeave={() => setDragOver(false)}
                      onDrop={(e) => {
                        e.preventDefault();
                        setDragOver(false);
                        addDocs(e.dataTransfer.files);
                      }}
                      role="button"
                      tabIndex={0}
                      aria-label="Dodaj dokumenty do analizy"
                    >
                      <span className="dz-ic">
                        <Clip />
                      </span>
                      <span className="dz-txt">
                        <b>Kliknij, aby dodać pliki</b> lub przeciągnij je tutaj
                        <small>
                          Umowa kredytowa, aneksy, zaświadczenie z banku · PDF,
                          JPG, PNG, DOC · do {DOC_MAX_FILES} plików, maks.{" "}
                          {DOC_MAX_MB} MB każdy
                        </small>
                      </span>
                      <input
                        ref={fileInputRef}
                        id="docs"
                        type="file"
                        multiple
                        accept={DOC_ACCEPT}
                        hidden
                        onChange={(e) => addDocs(e.target.files)}
                      />
                    </div>
                    {docErr && <div className="err">{docErr}</div>}
                    {docs.length > 0 && (
                      <ul className="doc-list">
                        {docs.map((d, i) => (
                          <li key={`${d.name}-${d.size}`} className="doc-item">
                            <span className="doc-ic">
                              <FileIc />
                            </span>
                            <span className="doc-name" title={d.name}>
                              {d.name}
                            </span>
                            <span className="doc-size num">
                              {fmtSize(d.size)}
                            </span>
                            <button
                              type="button"
                              className="doc-del"
                              onClick={() => removeDoc(i)}
                              aria-label={`Usuń plik ${d.name}`}
                            >
                              <X />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                    <p className="lab-note">
                      Dokumenty przekazujemy wyłącznie prawnikowi prowadzącemu
                      Twoją sprawę.
                    </p>
                  </div>
                )}

                <div className="full">
                  <div className="consent">
                    <input
                      id="consent"
                      type="checkbox"
                      checked={f.consent}
                      onChange={(e) => upd("consent", e.target.checked)}
                    />
                    <label htmlFor="consent">
                      Zapoznałem/-am się z{" "}
                      <a
                        href="https://kppp.pl/polityka-prywatnosci/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        polityką prywatności
                      </a>{" "}
                      i akceptuję jej postanowienia. Wyrażam zgodę na
                      przetwarzanie danych w celu kontaktu (art. 6 ust. 1 lit. a
                      RODO). <span className="req">*</span>
                    </label>
                  </div>
                  {errs.consent && (
                    <div className="err">
                      Zgoda jest wymagana, aby przesłać zgłoszenie.
                    </div>
                  )}
                </div>
              </div>

              <div className="nav-row">
                <button className="btn btn-ghost" onClick={() => goStep(2)}>
                  <Back />
                  Wstecz
                </button>
                <button
                  className="btn btn-primary"
                  onClick={mode === "ai" ? onAnalyzeAi : onCalc}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spin" />{" "}
                      {mode === "ai" ? "Analizuję umowę…" : "Liczę…"}
                    </>
                  ) : (
                    <>
                      Pokaż wynik analizy
                      <Arrow />
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* ================= KROK 4 — WYNIK ================= */}
          {step === 4 && (
            <div className="step">
              {errBanner && <div className="banner error">{errBanner}</div>}
              {docsNotice && <div className="banner notice">{docsNotice}</div>}
              {result && (
                <Result r={result} f={f} bank={bank} onReset={reset} />
              )}
              {/* Ścieżka 1: ustalenia AI ze skanu umowy */}
              {aiAnalysis && (
                <ChfFindings
                  extracted={aiAnalysis.extracted}
                  eligibility={aiAnalysis.eligibility}
                  matchedMapClauses={aiAnalysis.matchedMapClauses}
                />
              )}
              {/* Ścieżka 2: propozycja analizy umowy PO kalkulacji —
                  tylko gdy CRM zwrócił leadId i nie było analizy na wejściu */}
              {leadId && mode === "calc" && !aiAnalysis && (
                <ContractAnalysisPanel
                  contractDocs={contractDocs}
                  contractErr={contractErr}
                  contractBusy={contractBusy}
                  contractAnalysis={contractAnalysis}
                  contractInputRef={contractInputRef}
                  onAddFiles={addContractDocs}
                  onRemoveFile={removeContractDoc}
                  onAnalyze={analyzeContractForLead}
                />
              )}
              {!result && !aiAnalysis && !errBanner && (
                <p className="center">Brak wyniku.</p>
              )}
              {!result && errBanner && (
                <div className="result-cta">
                  <button className="btn btn-primary" onClick={() => goStep(3)}>
                    Wróć do formularza
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------- panel wyniku ---------- */
function Row({ title, sub, val, cls = "" }) {
  const v = typeof val === "number" && isFinite(val) ? pln2.format(val) : "—";
  return (
    <div className={`lrow ${cls}`}>
      <div className="k">
        {title}
        <small>{sub}</small>
      </div>
      <div className="v num">{v}</div>
    </div>
  );
}

function Result({ r, f, bank, onReset }) {
  const benefit = r.lacznaKorzysc ?? 0;
  const positive = benefit > 0;
  const startInfo = `${MONTHS[parseInt(f.month, 10) - 1] || ""} ${f.year}`;

  return (
    <div className="step">
      <div className="ledger-top">
        <div className="cap">
          Szacowana łączna korzyść z unieważnienia umowy
        </div>
        <div className="big num">{plnFmt.format(Math.max(0, benefit))}</div>
        <div className="note">
          {positive
            ? "Suma nadpłaty ponad wypłacony kapitał oraz spadku salda zadłużenia po unieważnieniu umowy."
            : "Na podstawie podanych danych nadpłata nie powstała jeszcze w pełni — szczegóły omówi nasz prawnik."}
        </div>
      </div>

      <div className="ledger-rows">
        <Row
          title="Nadpłata ponad kapitał"
          sub="ile zapłacono ponad wypłaconą kwotę"
          val={r.nadplata}
          cls="gain"
        />
        <Row
          title="Spadek salda zadłużenia"
          sub="o ile maleje saldo wg unieważnienia"
          val={r.zmniejszenieSalda}
          cls="gain"
        />
        <Row
          title="Obecne saldo wg banku"
          sub="ile bank wykazuje dziś do spłaty"
          val={r.currentBalancePLN}
          cls="warn"
        />
        <Row
          title="Saldo po unieważnieniu"
          sub="ile kapitału pozostałoby do zwrotu"
          val={r.saldoPoUniewaznieniu}
        />
        <Row
          title="Suma zapłacona bankowi"
          sub="wszystkie raty do dziś"
          val={r.sumaZaplaconaBanku}
        />
        <div className="lrow total">
          <div className="k">Łączna korzyść (szacunek)</div>
          <div className="v num">{plnFmt.format(Math.max(0, benefit))}</div>
        </div>
      </div>

      <div className="meta-strip">
        <span className="chip">
          <b>Bank:</b> {r.bankName || bank || "—"}
        </span>
        <span className="chip">
          <b>Waluta:</b> {r.currency}
        </span>
        <span className="chip">
          <b>Uruchomienie:</b> {startInfo}
        </span>
        <span className="chip">
          <b>Kwota kredytu:</b> {pln2.format(r.loanAmountPLN || 0)}
        </span>
        {r.startRate != null && (
          <span className="chip">
            <b>Kurs startowy:</b> {Number(r.startRate).toFixed(4)}
          </span>
        )}
        <span className="chip">
          <b>Rat zapłaconych:</b> {r.paidInstallments}
        </span>
      </div>

      <div className="next-box">
        <div className="next-title">Co dalej?</div>
        <p>
          Dziękujemy za skorzystanie z kalkulatora. Niebawem skontaktujemy się,
          aby bezpłatnie omówić pełną analizę Twojej umowy. Możesz też zadzwonić
          od razu: <a href="tel:+48608649107">+48 608 64 91 07</a>.
        </p>
      </div>

      <p className="disclaim">
        Wynik ma charakter wyłącznie szacunkowy, oparty o dane wprowadzone w
        formularzu oraz kursy NBP i stawki referencyjne z bazy. Nie stanowi
        porady prawnej ani gwarancji rozstrzygnięcia. Ostateczna ocena wymaga
        analizy pełnej dokumentacji kredytowej.
      </p>

      <div className="result-cta">
        <button className="btn btn-ghost" onClick={onReset}>
          Policz jeszcze raz
        </button>
      </div>
    </div>
  );
}

const CLAUSE_EFFECT_LABEL = {
  niewaznosc: "może skutkować unieważnieniem umowy",
  zwrot_nadplat: "może skutkować zwrotem nadpłaconych kwot",
};

/* ---------- ustalenia AI (wspólne dla ścieżki 1 i 2) ---------- */
function ChfFindings({ extracted, eligibility, matchedMapClauses, footer }) {
  return (
    <div className="panel-note ok">
      <h3>Wirtualny asystent prawny — sprawa kwalifikuje się do analizy</h3>
      {extracted?.bankName && (
        <p>
          Rozpoznany bank: <b>{extracted.bankName}</b>
          {extracted.agreementDate
            ? `, umowa z ${extracted.agreementDate}`
            : ""}
        </p>
      )}
      {!!eligibility?.reasons?.length && (
        <ul className="ok-list">
          {eligibility.reasons.map((r, i) => (
            <li key={i}>{r}</li>
          ))}
        </ul>
      )}
      {!!extracted?.qualifyingClauses?.length && (
        <>
          <p>Zapisy potencjalnie abuzywne wskazane w Twojej umowie:</p>
          <ul className="clause-list">
            {extracted.qualifyingClauses.map((c, i) => (
              <li key={i}>
                „{c.clauseQuote}”{c.location ? ` (${c.location})` : ""} —{" "}
                {c.reasonForAbusive}
              </li>
            ))}
          </ul>
        </>
      )}
      {!!matchedMapClauses?.clauses?.length && (
        <>
          <p>
            Podobne klauzule dla banku „{matchedMapClauses.bank}” znalezione w
            mapie klauzul niedozwolonych Rzecznika Finansowego:
          </p>
          <ul className="clause-list map-list">
            {matchedMapClauses.clauses.slice(0, 6).map((c, i) => (
              <li key={i}>
                „{c.quote}”{c.location ? ` (${c.location})` : ""} —{" "}
                {CLAUSE_EFFECT_LABEL[c.effect] || c.effect}
                {c.agreement ? ` · ${c.agreement}` : ""}
              </li>
            ))}
          </ul>
        </>
      )}
      <p className="step-sub">
        {footer ||
          "To wstępna analiza automatyczna — nie stanowi porady prawnej. " +
            "Ostateczną ocenę kwalifikacji przeprowadzi bezpłatnie nasz prawnik, " +
            "który otrzymał już to zestawienie wraz z Twoim zgłoszeniem."}
      </p>
    </div>
  );
}

/* ---------- panel analizy AI umowy (ścieżka 2, po wyniku) ---------- */
function ContractAnalysisPanel({
  contractDocs,
  contractErr,
  contractBusy,
  contractAnalysis,
  contractInputRef,
  onAddFiles,
  onRemoveFile,
  onAnalyze,
}) {
  if (contractAnalysis) {
    return (
      <>
        <ChfFindings
          extracted={contractAnalysis.extracted}
          eligibility={contractAnalysis.eligibility}
          matchedMapClauses={contractAnalysis.matchedMapClauses}
          footer={
            "Skan umowy i wynik analizy zostały dopięte do Twojego zgłoszenia — " +
            "prawnik skontaktuje się w razie pytań. Analiza automatyczna nie " +
            "stanowi porady prawnej."
          }
        />
        {contractErr && <div className="err">{contractErr}</div>}
      </>
    );
  }

  return (
    <div className="panel-note">
      <h3>Masz skan umowy pod ręką?</h3>
      <p className="step-sub">
        Prześlij go, a wirtualny asystent prawny sprawdzi zapisy umowy w mapie
        klauzul niedozwolonych Rzecznika Finansowego — dołączymy to do Twojego
        zgłoszenia.
      </p>
      <input
        ref={contractInputRef}
        type="file"
        multiple
        accept={DOC_ACCEPT}
        style={{ display: "none" }}
        onChange={(e) => onAddFiles(e.target.files)}
      />
      <button
        type="button"
        className="btn btn-ghost"
        onClick={() => contractInputRef.current?.click()}
        disabled={contractBusy}
      >
        Dodaj pliki umowy
      </button>
      {!!contractDocs.length && (
        <ul className="doc-list">
          {contractDocs.map((d, i) => (
            <li key={i}>
              {d.name} ({fmtSize(d.size)}){" "}
              <button
                type="button"
                className="doc-remove"
                onClick={() => onRemoveFile(i)}
                disabled={contractBusy}
              >
                usuń
              </button>
            </li>
          ))}
        </ul>
      )}
      {contractErr && <div className="err">{contractErr}</div>}
      {!!contractDocs.length && (
        <button
          type="button"
          className="btn btn-primary"
          onClick={onAnalyze}
          disabled={contractBusy}
        >
          {contractBusy ? "Analizuję…" : "Przeanalizuj umowę"}
        </button>
      )}
    </div>
  );
}
